/**
 * Shared AI-assistant realtime-stream subscription.
 *
 * Both the initial-turn policy (`backendPolicy`) and the resumed-turn policy
 * (`approvalPolicy`, after a HITL approval) need to subscribe to the SAME
 * realtime channel (`'aiAssistant'` / conversationId) and process the identical
 * `StreamUpdate` sequence into the assistant message. Factoring the handler here
 * means the resumed turn gets full parity with the initial turn — chunks, plans,
 * tool steps, a chained interrupt, and the terminal `done` — instead of a
 * narrowed copy.
 *
 * Lifecycle ownership stays with the caller: `subscribeToAssistantStream`
 * returns the `unsubscribe` fn and tears the subscription down itself on the
 * terminal `done` / `error` / `interrupt` updates. The caller unsubscribes on
 * its own failure paths (e.g. the POST rejecting).
 */

import { PolicyContext } from '@commandDrivenStateEngine/core/policies';
import type { RealtimeSession } from '@bett3r-dev/teselly-client-library';
import type {
  GeneratedReportBody,
  GenerationFailure,
  GenerationFailureReason
} from '@components/application/Reports/GenerateReport/generateReport';
import { AIAssistantState, ReportPreview } from '../state';
import {
  RULE_PREVIEW_BLOCK_TYPE,
  toRulePreviewPayload,
  type RulePreviewPayload
} from '../rulePreview';
import {
  AUTOMATION_DRAFT_BLOCK_TYPE,
  toAutomationDraftPayload
} from '../automationDraft';
import { AIAssistantCommand } from '../commands';

/**
 * StreamUpdate types received from the backend via WebSocket.
 * Mirrors StreamUpdateType from teselly-domain.
 *
 * The `interrupt` variant additionally carries an optional pending tool call
 * (toolName + args) so the approval UI can render the action being held. The
 * `question` field stays as the user-facing prompt text.
 */
export type StreamUpdate =
  | { type: 'status'; step: string }
  | { type: 'chunk'; node: string; content: string }
  | { type: 'tool_call'; name: string; args: unknown }
  | { type: 'tool_result'; name: string; result: unknown }
  | { type: 'plan'; plan: import( '@infrastructure/aiAssistant/types' ).AICommandPlan }
  | { type: 'done'; messageId: string; traceId?: string }
  | { type: 'error'; message: string }
  | {
      type: 'interrupt';
      question: string;
      toolName?: string;
      args?: Record<string, unknown>;
    };

/**
 * Humanize a snake_case tool name into Title Case for display.
 * e.g. `publish_listing` -> `Publish Listing`, `change_live_price` -> `Change Live Price`.
 */
export const humanizeToolName = ( name: string ): string => {
  if ( !name ) return '';
  return name
    .split( /[_\s-]+/ )
    .filter(( part ) => part.length > 0 )
    .map(( part ) => part.charAt( 0 ).toUpperCase() + part.slice( 1 ).toLowerCase())
    .join( ' ' );
};

/** Friendly labels for tool_call names shown as processing steps. */
const getToolCallLabel = ( name: string ): string => {
  const labels: Record<string, string> = {
    query_orders: 'Querying orders database...',
    query_publications: 'Querying publications...',
    query_inventory: 'Checking inventory...',
    query_warehouses: 'Looking up warehouses...'
  };
  return labels[ name ] || `Using ${name}...`;
};

const isRecord = ( value: unknown ): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * A layout worth rendering: present, with at least one widget. Mirrors the
 * Reports page's `hasNearestReport` guard — a `{ widgets: [] }` that slipped
 * through would render as a titled, empty grid, which reads as "broken", not as
 * a report. The banner (on failure) is the honest answer on its own; a preview
 * is a bonus or it is nothing.
 */
const isRenderableReport = ( value: unknown ): value is GeneratedReportBody => {
  if ( !isRecord( value )) return false;
  const layout = value.layout;
  return isRecord( layout )
    && Array.isArray( layout.widgets )
    && layout.widgets.length > 0;
};

const KNOWN_FAILURE_REASONS: readonly GenerationFailureReason[] = [
  'validation-exhausted', 'unanswerable', 'unknown'
];

const toFailureReason = ( reason: unknown ): GenerationFailureReason =>
  typeof reason === 'string'
    && KNOWN_FAILURE_REASONS.includes( reason as GenerationFailureReason )
    ? reason as GenerationFailureReason
    : 'unknown';

/**
 * Map the `generate_report` tool result — arriving as an opaque `tool_result`
 * StreamUpdate payload (`{ name, result }`, see `agentRuns.policy`) — to a chat
 * report-preview artifact.
 *
 * The S1 backend tool already classified the terminal 422 into
 * `{ ok: false, reason, ... }`, so this is a shape-narrowing adapter, NOT a
 * re-classification. It returns `undefined` for anything it does not recognise
 * (a malformed payload, an empty success layout) so the caller renders no card
 * rather than an empty grid. Only the caller's `update.name === 'generate_report'`
 * gate decides that this result is a report at all.
 */
export const toReportPreview = ( result: unknown ): ReportPreview | undefined => {
  if ( !isRecord( result )) return undefined;

  if ( result.ok === true ) {
    const body = { layout: result.layout };
    return isRenderableReport( body ) ? { status: 'success', body } : undefined;
  }

  if ( result.ok === false ) {
    const explanation = typeof result.explanation === 'string' ? result.explanation : undefined;
    const missingConcepts = Array.isArray( result.missingConcepts )
      ? result.missingConcepts.filter(( concept ): concept is string => typeof concept === 'string' )
      : undefined;
    const nearestReport = isRenderableReport( result.nearestReport ) ? result.nearestReport : undefined;
    const failure: GenerationFailure = {
      reason: toFailureReason( result.reason ),
      ...( explanation !== undefined ? { explanation } : {}),
      ...( missingConcepts !== undefined ? { missingConcepts } : {}),
      ...( nearestReport !== undefined ? { nearestReport } : {})
    };
    return { status: 'failure', failure };
  }

  return undefined;
};

/**
 * Map the `compose_rule` tool result — arriving as an opaque `tool_result`
 * payload — to the `rule-preview` block payload (TV1-2340 · P4).
 *
 * Same contract as `toReportPreview` above, and re-exported from here for the
 * same reason: the adapter belongs next to the only gate that decides the
 * result IS a composed rule, which is `update.name === 'compose_rule'`. The
 * body lives in `../rulePreview` because the dangling-edge guard it depends on
 * is shared with the renderer's error boundary.
 */
export const toRulePreview = ( result: unknown ): RulePreviewPayload | undefined =>
  toRulePreviewPayload( result );

type Ctx = PolicyContext<AIAssistantState, AIAssistantCommand>;

/**
 * Subscribe to the assistant realtime channel for a conversation and pump the
 * StreamUpdate sequence into `loadingMessageId`. Returns the `unsubscribe` fn;
 * the handler also self-unsubscribes on the terminal updates.
 *
 * Both the first turn and the post-approval resumed turn stream over the same
 * `( 'aiAssistant', conversationId )` channel, so the same handler serves both —
 * the resumed turn simply targets a fresh loading message.
 */
export const subscribeToAssistantStream = ( params: {
  realtimeSession: RealtimeSession | null;
  conversationId: string;
  loadingMessageId: string;
  ctx: Ctx;
}): (() => void ) => {
  const { realtimeSession, conversationId, loadingMessageId, ctx } = params;

  const handleStreamUpdate = ( update: StreamUpdate ) => {
    /**
     * Abandoned turn guard.
     *
     * Aborting a response settles its message (`message/settleLoading`) but the
     * socket keeps delivering — the backend has no idea the user pressed stop.
     * Without this, those late chunks land in a bubble the user already
     * considers finished, so an answer they cancelled starts writing itself
     * back in. The terminal updates are exempt: they carry the unsubscribe and
     * the processing reset that end the turn cleanly.
     */
    const isTerminal =
      update.type === 'done' || update.type === 'error' || update.type === 'interrupt';
    if ( !isTerminal ) {
      const state = ctx.getState();
      const target = state.messages.find(( m ) => m.id === loadingMessageId );
      /**
       * BOTH signals must agree the turn is over, and that is deliberate:
       *
       *  • `!isProcessing` alone is not enough to identify the message — and
       *  • "the bubble is not loading" alone is WRONG, because a `plan` update
       *    dispatches `setAssistantResponse`, which clears `isLoading` while the
       *    turn is very much still running. Guarding on that alone would drop
       *    every chunk that follows a plan.
       *
       * Together they describe exactly one situation: the turn stopped
       * processing AND its bubble was settled — which is what `abortAssistantResponse`
       * does, and nothing else does.
       */
      if ( !state.isProcessing && target && !target.isLoading ) {
        unsubscribe();
        return;
      }
    }

    switch ( update.type ) {
      case 'status':
        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: update.step }},
          { source: 'application' }
        );
        break;

      case 'chunk':
        ctx.dispatch(
          { type: 'aiAssistant/message/appendChunk', payload: {
            messageId: loadingMessageId,
            content: update.content
          }},
          { source: 'api' }
        );
        break;

      case 'tool_call':
        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: getToolCallLabel( update.name ) }},
          { source: 'application' }
        );
        break;

      case 'tool_result':
        // Only `generate_report` surfaces a chat artifact; every other read
        // tool's result is consumed by the model, not rendered. Keying on the
        // tool NAME is the contract — the backend fans a `tool-result` agent
        // event out as `tool_result{ name, result }` (agentRuns.policy).
        if ( update.name === 'generate_report' ) {
          const preview = toReportPreview( update.result );
          if ( preview ) {
            ctx.dispatch(
              { type: 'aiAssistant/message/setReportPreview', payload: {
                messageId: loadingMessageId,
                preview
              }},
              { source: 'api' }
            );
          }
        }

        /**
         * `compose_rule` — the prose-to-rule-graph tool (TV1-2340 · P4).
         *
         * Two dispatches, deliberately separate:
         *  • the BLOCK is what the user sees, and it is attached for every
         *    renderable outcome including the refusal and the unparseable
         *    graph — an honest failure card beats a silent nothing;
         *  • the GRAPH memo is only set for a graph we could actually draw.
         *    Remembering a refusal (or a graph the editor rejects) would feed
         *    the next turn something to "refine" that never existed.
         */
        if ( update.name === 'compose_rule' ) {
          const preview = toRulePreview( update.result );
          if ( preview ) {
            ctx.dispatch(
              { type: 'aiAssistant/message/setBlock', payload: {
                messageId: loadingMessageId,
                block: { type: RULE_PREVIEW_BLOCK_TYPE, payload: preview }
              }},
              { source: 'api' }
            );
            if ( preview.status === 'graph' ) {
              ctx.dispatch(
                { type: 'aiAssistant/composer/setLastComposedGraph', payload: { graph: preview.graph }},
                { source: 'api' }
              );
            }
          }
        }
        /**
         * `propose_automation` / `refine_sub_goal` — the interview's chat
         * artifact (TV1-2382).
         *
         * Both tools return the SAME shape, and both must land on the card: a
         * refinement that did not re-render would leave the seller agreeing to a
         * draft that is no longer the one the server holds. The block carries the
         * `draftHash`, which Submit sends back — the server refuses a payload
         * whose hash does not match the decomposition she saw.
         *
         * Keying on the tool NAME is the contract, same as the two above.
         */
        if ( update.name === 'propose_automation' || update.name === 'refine_sub_goal' ) {
          const payload = toAutomationDraftPayload( update.result );
          if ( payload ) {
            ctx.dispatch(
              { type: 'aiAssistant/message/setBlock', payload: {
                messageId: loadingMessageId,
                block: { type: AUTOMATION_DRAFT_BLOCK_TYPE, payload }
              }},
              { source: 'api' }
            );
          }
        }
        break;

      case 'plan':
        ctx.dispatch(
          { type: 'aiAssistant/message/setAssistantResponse', payload: {
            messageId: loadingMessageId,
            content: ctx.getState().messages.find( m => m.id === loadingMessageId )?.content || '',
            commandPlan: update.plan
          }},
          { source: 'api' }
        );
        break;

      case 'done':
        unsubscribe();
        // TRACE ID FIRST, before the bubble settles. `settleLoading` is what makes the
        // message render its artifacts and its feedback control (`AIAssistantChat`
        // gates both on `!isLoading`), so attaching the trace afterwards would expose
        // a one-render window where the thumbs are clickable with nothing to score.
        //
        // Absent on almost every run today — no deployed environment configures the
        // Langfuse exporter — so this is a conditional dispatch, not a defaulted one.
        if ( update.traceId ) {
          ctx.dispatch(
            { type: 'aiAssistant/message/setTraceId', payload: {
              messageId: loadingMessageId,
              traceId: update.traceId
            }},
            { source: 'api' }
          );
        }
        // SETTLE THE BUBBLE. Until TV1-2340 this case cleared only the processing
        // flags, so `isLoading` stayed true on any turn that ended in plain text —
        // the ONLY happy-path clearers are `plan` and `interrupt` (via
        // `setAssistantResponse`); everything else that clears it is an error path.
        //
        // That matters because `AIAssistantChat` gates EVERY message artifact on
        // `!message.isLoading`. A turn shaped "tool call -> tool result -> prose ->
        // done" therefore renders its artifact NEVER — silently, with the streamed
        // text visible above it, so it reads as the tool having returned nothing.
        // `compose_rule` is exactly that shape, and `generate_report` has always
        // been too.
        //
        // `settleLoading` (not `setAssistantResponse`) is the right instrument: it
        // keeps whatever streamed in and only substitutes a fallback for a bubble
        // that received nothing, so an answer already on screen is never replaced.
        ctx.dispatch(
          { type: 'aiAssistant/message/settleLoading', payload: {}},
          { source: 'api' }
        );
        ctx.dispatch(
          { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
          { source: 'application' }
        );
        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
          { source: 'application' }
        );
        break;

      case 'error':
        unsubscribe();
        ctx.dispatch(
          { type: 'aiAssistant/message/setAssistantResponse', payload: {
            messageId: loadingMessageId,
            content: update.message || 'Sorry, I encountered an error processing your request. Please try again.'
          }},
          { source: 'api' }
        );
        ctx.dispatch(
          { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
          { source: 'application' }
        );
        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
          { source: 'application' }
        );
        break;

      case 'interrupt':
        unsubscribe();
        ctx.dispatch(
          { type: 'aiAssistant/message/setAssistantResponse', payload: {
            messageId: loadingMessageId,
            content: update.question
          }},
          { source: 'api' }
        );
        // If the interrupt carries a pending tool call, surface it to the
        // approval UI. The backend mints the approval token after the user
        // approves and the approval policy issues ResumeAgentRun.
        if ( update.toolName ) {
          ctx.dispatch(
            { type: 'aiAssistant/approval/setPending', payload: {
              toolName: update.toolName,
              humanizedName: humanizeToolName( update.toolName ),
              args: update.args || {},
              conversationId
            }},
            { source: 'api' }
          );
        }
        ctx.dispatch(
          { type: 'aiAssistant/processing/setProcessing', payload: { isProcessing: false }},
          { source: 'application' }
        );
        ctx.dispatch(
          { type: 'aiAssistant/processing/setStep', payload: { step: '' }},
          { source: 'application' }
        );
        break;
    }
  };

  // Subscribe to the AI assistant WebSocket channel for this conversation.
  // Backend sends messages with type='aiAssistant' and ref=conversationId.
  const unsubscribe = (() => {
    if ( realtimeSession ) {
      realtimeSession.onMessage<StreamUpdate>( 'aiAssistant', conversationId, handleStreamUpdate );
      return () => {
        realtimeSession.offMessage<StreamUpdate>( 'aiAssistant', conversationId, handleStreamUpdate );
      };
    }
    return () => {};
  })();

  return unsubscribe;
};
